const SCHEMA_VERSION = 1;
const DEBUG_QUERY_PARAMETER = 'abm_analytics_debug';
const PARTNER_FORM_PENDING_KEY = 'abm_partner_application_pending_v1';
const MAX_DEBUG_EVENTS = 100;
const MAX_RESULT_COUNT = 250;

export const ABM_ANALYTICS_EVENT_NAMES = Object.freeze([
  'abm_search_submitted',
  'abm_directory_results_viewed',
  'abm_zero_results',
  'abm_workshop_profile_viewed',
  'abm_workshop_contact_clicked',
  'abm_workshop_claim_clicked',
  'abm_partner_application_started',
  'abm_partner_application_submitted',
]);

const EVENT_NAMES = new Set(ABM_ANALYTICS_EVENT_NAMES);
const VEHICLES = new Set(['bicycle', 'ebike', 'scooter']);
const SERVICES = new Set([
  'tyres',
  'brakes',
  'drivetrain',
  'battery',
  'motor',
  'diagnostics',
  'electronics',
  'general-maintenance',
]);
const PAGE_TYPES = new Set(['homepage', 'directory', 'workshop_profile', 'partner', 'other']);
const SOURCE_PAGES = new Set(['homepage', 'directory', 'workshop_profile', 'partner', 'direct', 'other']);
const PARTNER_TIERS = new Set(['none', 'partner', 'partner_pro']);
const CLAIM_STATUSES = new Set(['unclaimed', 'claimed']);
const VERIFICATION_STATUSES = new Set(['unverified', 'verified']);
const CONTACT_TYPES = new Set(['phone', 'email', 'whatsapp']);
const INTERACTION_SOURCES = new Set([
  'card',
  'profile_primary',
  'profile_final',
  'claim_disclosure',
  'partner_form',
]);
const ROME_ALIASES = new Set(['roma', 'rome', 'rm', 'roma capitale']);
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HANDLE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * @typedef {object} AbmLocationContext
 * @property {boolean} location_present
 * @property {'market' | 'postal_area' | 'unclassified'} [location_kind]
 * @property {string} [location_area]
 */

/**
 * @typedef {object} AbmAnalyticsContext
 * @property {string} [pagePath]
 * @property {string} [pageType]
 */

/**
 * @typedef {object} AbmPublishOptions
 * @property {string} [dedupeScope]
 * @property {string} [dedupeValue]
 */

/** @param {unknown} value */
function asString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/** @param {unknown} value */
function normalizeText(value) {
  return asString(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {unknown} value
 * @returns {'bicycle' | 'ebike' | 'scooter' | undefined}
 */
export function normalizeAbmVehicle(value) {
  const normalized = normalizeText(value);
  return VEHICLES.has(normalized) ? /** @type {'bicycle' | 'ebike' | 'scooter'} */ (normalized) : undefined;
}

/**
 * @param {unknown} value
 * @returns {'tyres' | 'brakes' | 'drivetrain' | 'battery' | 'motor' | 'diagnostics' | 'electronics' | 'general-maintenance' | undefined}
 */
export function normalizeAbmService(value) {
  const normalized = normalizeText(value);
  const canonical = normalized === 'other' ? 'general-maintenance' : normalized;
  return SERVICES.has(canonical)
    ? /** @type {'tyres' | 'brakes' | 'drivetrain' | 'battery' | 'motor' | 'diagnostics' | 'electronics' | 'general-maintenance'} */ (
        canonical
      )
    : undefined;
}

/**
 * Converts approved markets and Italian CAPs into coarse analytics dimensions.
 * Unknown free text is never returned.
 *
 * @param {unknown} value
 * @returns {AbmLocationContext}
 */
export function minimizeAbmLocation(value) {
  const normalized = normalizeText(value);

  if (normalized === '') return { location_present: false };

  if (ROME_ALIASES.has(normalized)) {
    return {
      location_present: true,
      location_kind: 'market',
      location_area: 'roma',
    };
  }

  if (/^\d{5}$/.test(normalized)) {
    return {
      location_present: true,
      location_kind: 'postal_area',
      location_area: `${normalized.slice(0, 3)}xx`,
    };
  }

  return {
    location_present: true,
    location_kind: 'unclassified',
  };
}

/** @param {unknown} value @param {Set<string>} values */
function allowedValue(value, values) {
  const normalized = asString(value);
  return values.has(normalized) ? normalized : undefined;
}

/** @param {unknown} value */
function validAbmId(value) {
  const normalized = asString(value);
  return UUID_PATTERN.test(normalized) ? normalized.toLowerCase() : undefined;
}

/** @param {unknown} value */
function validWorkshopHandle(value) {
  const normalized = asString(value).toLocaleLowerCase('en');
  return HANDLE_PATTERN.test(normalized) ? normalized : undefined;
}

/** @param {unknown} value */
function validResultCount(value) {
  return Number.isInteger(value) && Number(value) >= 0 && Number(value) <= MAX_RESULT_COUNT
    ? Number(value)
    : undefined;
}

/** @param {unknown} value */
function normalizePagePath(value) {
  const path = asString(value);
  if (!path.startsWith('/') || path.length > 240) return '/';
  return path.split(/[?#]/, 1)[0] || '/';
}

/** @param {string} pagePath */
function inferPageType(pagePath) {
  if (pagePath === '/') return 'homepage';
  if (pagePath === '/pages/officina-nazionale') return 'directory';
  if (pagePath.startsWith('/pages/officine/')) return 'workshop_profile';
  if (pagePath === '/pages/per-le-officine') return 'partner';
  return 'other';
}

/** @returns {AbmAnalyticsContext} */
function getRuntimeContext() {
  if (typeof window === 'undefined') return { pagePath: '/', pageType: 'other' };
  const pagePath = normalizePagePath(window.location.pathname);
  return { pagePath, pageType: inferPageType(pagePath) };
}

/**
 * @param {unknown} eventName
 * @param {Record<string, unknown>} [properties]
 * @param {AbmAnalyticsContext} [context]
 * @returns {Record<string, unknown> | null}
 */
export function buildAbmAnalyticsEvent(eventName, properties = {}, context = {}) {
  if (typeof eventName !== 'string' || !EVENT_NAMES.has(eventName)) return null;

  const pagePath = normalizePagePath(context.pagePath ?? '/');
  const requestedPageType = allowedValue(context.pageType, PAGE_TYPES);
  const payload = {
    schema_version: SCHEMA_VERSION,
    event_name: eventName,
    page_type: requestedPageType ?? inferPageType(pagePath),
    page_path: pagePath,
  };

  const sourcePage = allowedValue(properties.source_page, SOURCE_PAGES);
  const vehicle = normalizeAbmVehicle(properties.vehicle);
  const service = normalizeAbmService(properties.service);
  const location = minimizeAbmLocation(properties.location);
  const resultCount = validResultCount(properties.result_count);
  const abmId = validAbmId(properties.abm_id);
  const workshopHandle = validWorkshopHandle(properties.workshop_handle ?? properties.handle);
  const partnerTier = allowedValue(properties.partner_tier, PARTNER_TIERS);
  const claimStatus = allowedValue(properties.claim_status, CLAIM_STATUSES);
  const verificationStatus = allowedValue(properties.verification_status, VERIFICATION_STATUSES);
  const contactType = allowedValue(properties.contact_type, CONTACT_TYPES);
  const interactionSource = allowedValue(properties.interaction_source, INTERACTION_SOURCES);

  if (sourcePage) payload.source_page = sourcePage;
  if (vehicle) payload.vehicle = vehicle;
  if (service) payload.service = service;

  if (location.location_present) {
    payload.location_present = true;
    if (location.location_kind) payload.location_kind = location.location_kind;
    if (location.location_area) payload.location_area = location.location_area;
  } else if (eventName.startsWith('abm_search_') || eventName.includes('directory') || eventName === 'abm_zero_results') {
    payload.location_present = false;
  }

  if (typeof properties.partner_filter === 'boolean') payload.partner_filter = properties.partner_filter;
  if (resultCount !== undefined) payload.result_count = resultCount;
  if (abmId) payload.abm_id = abmId;
  if (workshopHandle) payload.workshop_handle = workshopHandle;
  if (partnerTier) payload.partner_tier = partnerTier;
  if (claimStatus) payload.claim_status = claimStatus;
  if (verificationStatus) payload.verification_status = verificationStatus;
  if (contactType) payload.contact_type = contactType;
  if (interactionSource) payload.interaction_source = interactionSource;

  const isWorkshopEvent = [
    'abm_workshop_profile_viewed',
    'abm_workshop_contact_clicked',
    'abm_workshop_claim_clicked',
  ].includes(eventName);

  if (isWorkshopEvent && (!abmId || !workshopHandle)) return null;
  if (eventName === 'abm_workshop_contact_clicked' && !contactType) return null;
  if (eventName === 'abm_workshop_claim_clicked' && interactionSource !== 'claim_disclosure') return null;
  if (eventName === 'abm_directory_results_viewed' && resultCount === undefined) return null;
  if (eventName === 'abm_zero_results' && resultCount !== 0) return null;

  return payload;
}

/** @param {string} eventName @param {Record<string, unknown>} payload */
function publishToShopify(eventName, payload) {
  try {
    const runtime = /** @type {any} */ (globalThis);
    const publish = runtime.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return;

    const result = publish.call(runtime.Shopify.analytics, eventName, payload);
    if (result && typeof result.catch === 'function') result.catch(() => {});
  } catch (_error) {
    // Analytics is deliberately fail-open: the product action always continues.
  }
}

function isDebugEnabled() {
  try {
    if (typeof window === 'undefined') return false;
    return new URL(window.location.href).searchParams.get(DEBUG_QUERY_PARAMETER) === '1';
  } catch (_error) {
    return false;
  }
}

/** @param {Record<string, unknown>} payload */
function recordDebugEvent(payload) {
  if (!isDebugEnabled()) return;

  try {
    const runtime = /** @type {any} */ (globalThis);
    const events = Array.isArray(runtime.__ABM_ANALYTICS_DEBUG_EVENTS__)
      ? runtime.__ABM_ANALYTICS_DEBUG_EVENTS__
      : [];
    events.push(structuredClone(payload));
    if (events.length > MAX_DEBUG_EVENTS) events.splice(0, events.length - MAX_DEBUG_EVENTS);
    runtime.__ABM_ANALYTICS_DEBUG_EVENTS__ = events;

    if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent('abm:analytics-debug', { detail: structuredClone(payload) }));
    }
  } catch (_error) {
    // Debug inspection must be as fail-safe as the production transport.
  }
}

/**
 * Creates an isolated publisher. Tests and a future adapter can inject a transport
 * without changing presentation components.
 *
 * @param {{
 *   transport?: (eventName: string, payload: Record<string, unknown>) => unknown,
 *   contextProvider?: () => AbmAnalyticsContext,
 *   debugSink?: (payload: Record<string, unknown>) => void
 * }} [configuration]
 */
export function createAbmAnalyticsPublisher(configuration = {}) {
  const transport = configuration.transport ?? publishToShopify;
  const contextProvider = configuration.contextProvider ?? getRuntimeContext;
  const debugSink = configuration.debugSink ?? recordDebugEvent;
  const lastValueByScope = new Map();

  return Object.freeze({
    /**
     * @param {unknown} eventName
     * @param {Record<string, unknown>} [properties]
     * @param {AbmPublishOptions} [options]
     * @returns {Record<string, unknown> | null}
     */
    publish(eventName, properties = {}, options = {}) {
      try {
        const payload = buildAbmAnalyticsEvent(eventName, properties, contextProvider());
        if (!payload) return null;

        const dedupeScope = asString(options.dedupeScope);
        const dedupeValue = asString(options.dedupeValue);
        if (dedupeScope && dedupeValue) {
          if (lastValueByScope.get(dedupeScope) === dedupeValue) return null;
          lastValueByScope.set(dedupeScope, dedupeValue);
        }

        try {
          debugSink(payload);
        } catch (_error) {
          // Debug consumers are optional and may never affect UI behavior.
        }

        try {
          const result = transport(String(eventName), payload);
          if (result && typeof /** @type {any} */ (result).catch === 'function') {
            /** @type {Promise<unknown>} */ (result).catch(() => {});
          }
        } catch (_error) {
          // The event is still valid even when the optional transport is unavailable.
        }

        return payload;
      } catch (_error) {
        return null;
      }
    },
  });
}

export const ABMAnalytics = createAbmAnalyticsPublisher();

/**
 * Creates a privacy-safe key for directory event deduplication. Unknown location
 * strings collapse to the same unclassified bucket and are never retained.
 *
 * @param {{vehicle?: unknown, service?: unknown, location?: unknown, partner?: unknown}} state
 * @param {number} resultCount
 */
export function createAbmDirectoryStateKey(state, resultCount) {
  const location = minimizeAbmLocation(state.location);
  return JSON.stringify({
    vehicle: normalizeAbmVehicle(state.vehicle) ?? '',
    service: normalizeAbmService(state.service) ?? '',
    location_kind: location.location_kind ?? '',
    location_area: location.location_area ?? '',
    partner_filter: state.partner === true,
    result_count: validResultCount(resultCount) ?? -1,
  });
}

/** @param {Element} element */
function getWorkshopContext(element) {
  const root = element.closest('[data-abm-workshop-profile], [data-abm-workshop]');
  if (!(root instanceof HTMLElement)) return {};

  return {
    abm_id: root.dataset.abmId,
    workshop_handle: root.dataset.abmHandle,
    partner_tier: root.dataset.abmPartnerTier,
    claim_status: root.dataset.abmClaimStatus,
    verification_status: root.dataset.abmVerificationStatus,
  };
}

export function getAbmReferrerContext() {
  try {
    if (typeof document === 'undefined' || document.referrer === '') return { source_page: 'direct' };
    const referrer = new URL(document.referrer);
    if (referrer.origin !== window.location.origin) return { source_page: 'other' };

    if (referrer.pathname === '/pages/officina-nazionale') {
      return {
        source_page: 'directory',
        vehicle: referrer.searchParams.get('vehicle') ?? '',
        service: referrer.searchParams.get('service') ?? '',
        location: referrer.searchParams.get('location') ?? '',
      };
    }

    if (referrer.pathname === '/') return { source_page: 'homepage' };
    if (referrer.pathname === '/pages/per-le-officine') return { source_page: 'partner' };
    if (referrer.pathname.startsWith('/pages/officine/')) return { source_page: 'workshop_profile' };
    return { source_page: 'other' };
  } catch (_error) {
    return { source_page: 'other' };
  }
}

function getAbmInteractionContext() {
  try {
    if (window.location.pathname === '/pages/officina-nazionale') {
      const url = new URL(window.location.href);
      return {
        source_page: 'directory',
        vehicle: url.searchParams.get('vehicle') ?? '',
        service: url.searchParams.get('service') ?? '',
        location: url.searchParams.get('location') ?? '',
      };
    }
  } catch (_error) {
    return { source_page: 'other' };
  }

  return getAbmReferrerContext();
}

/** @param {HTMLFormElement} form */
function publishHomepageSearch(form) {
  try {
    const data = new FormData(form);
    ABMAnalytics.publish('abm_search_submitted', {
      vehicle: data.get('vehicle'),
      service: data.get('service'),
      location: data.get('location'),
      source_page: form.dataset.abmSourcePage ?? 'homepage',
    });
  } catch (_error) {
    // Native GET submission continues without analytics.
  }
}

function setPartnerFormPending() {
  try {
    window.sessionStorage.setItem(PARTNER_FORM_PENDING_KEY, '1');
  } catch (_error) {
    // The form submission must not depend on storage availability.
  }
}

function consumePartnerFormPending() {
  try {
    const isPending = window.sessionStorage.getItem(PARTNER_FORM_PENDING_KEY) === '1';
    if (isPending) window.sessionStorage.removeItem(PARTNER_FORM_PENDING_KEY);
    return isPending;
  } catch (_error) {
    return false;
  }
}

/** @param {ParentNode} root */
function scanAnalyticsMarkers(root) {
  const profiles = [];
  if (root instanceof Element && root.matches('[data-abm-workshop-profile]')) profiles.push(root);
  profiles.push(...root.querySelectorAll('[data-abm-workshop-profile]'));

  for (const profile of profiles) {
    if (!(profile instanceof HTMLElement) || profile.dataset.abmAnalyticsViewed === 'true') continue;
    profile.dataset.abmAnalyticsViewed = 'true';
    if (profile.dataset.abmIsDemo === 'true') continue;

    const context = { ...getWorkshopContext(profile), ...getAbmReferrerContext() };
    ABMAnalytics.publish('abm_workshop_profile_viewed', context, {
      dedupeScope: `workshop-profile-view:${context.abm_id ?? 'invalid'}`,
      dedupeValue: window.location.pathname,
    });
  }

  const successMarkers = [];
  if (root instanceof Element && root.matches('[data-abm-partner-form-success]')) successMarkers.push(root);
  successMarkers.push(...root.querySelectorAll('[data-abm-partner-form-success]'));

  for (const marker of successMarkers) {
    if (!(marker instanceof HTMLElement) || marker.dataset.abmAnalyticsSubmitted === 'true') continue;
    marker.dataset.abmAnalyticsSubmitted = 'true';
    if (!consumePartnerFormPending()) continue;

    ABMAnalytics.publish(
      'abm_partner_application_submitted',
      { source_page: 'partner', interaction_source: 'partner_form' },
      { dedupeScope: 'partner-application-submitted', dedupeValue: window.location.pathname }
    );
  }
}

function initializeAbmAnalyticsRuntime() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const runtime = /** @type {any} */ (globalThis);
  if (runtime.__ABM_ANALYTICS_RUNTIME_V1__) return;
  runtime.__ABM_ANALYTICS_RUNTIME_V1__ = true;

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (form.matches('[data-abm-search-form]')) publishHomepageSearch(form);
    if (form.matches('form.abm-partner-form')) setPartnerFormPending();
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const contact = target.closest('[data-abm-contact-type]');
    if (contact instanceof HTMLElement) {
      ABMAnalytics.publish('abm_workshop_contact_clicked', {
        ...getWorkshopContext(contact),
        contact_type: contact.dataset.abmContactType,
        interaction_source: contact.dataset.abmInteractionSource,
        ...getAbmInteractionContext(),
      });
      return;
    }

    const claim = target.closest('[data-abm-claim]');
    if (claim instanceof HTMLElement) {
      ABMAnalytics.publish('abm_workshop_claim_clicked', {
        ...getWorkshopContext(claim),
        interaction_source: 'claim_disclosure',
        ...getAbmReferrerContext(),
      });
    }
  });

  document.addEventListener('focusin', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const form = target.closest('form.abm-partner-form');
    if (!(form instanceof HTMLFormElement) || target.matches('button, input[type="hidden"]')) return;

    ABMAnalytics.publish(
      'abm_partner_application_started',
      { source_page: 'partner', interaction_source: 'partner_form' },
      { dedupeScope: 'partner-application-started', dedupeValue: window.location.pathname }
    );
  });

  scanAnalyticsMarkers(document);

  if (typeof MutationObserver === 'function') {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) scanAnalyticsMarkers(node);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAbmAnalyticsRuntime, { once: true });
  } else {
    initializeAbmAnalyticsRuntime();
  }
}
